import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BASE_URL from '../../../config/config';

const validateField = (name, value) => {
  switch (name) {
    case 'comment_text':
      // اختیاری - اگر پر شد، حداقل ۵ کاراکتر باشد
      if (value && value.trim().length > 0 && value.trim().length < 5) {
        return "متن نظر باید حداقل ۵ کاراکتر داشته باشد";
      }
      if (value && value.length > 2000) {
        return "متن نظر نباید بیشتر از 2000 کاراکتر باشد";
      }
      break;

    case 'user_email':
      // اختیاری - اگر پر شد، فرمت ایمیل را چک کند
      if (value && value.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "ایمیل وارد شده معتبر نیست";
        }
      }
      break;

    case 'mobile':
      // اختیاری - اگر پر شد، فرمت موبایل را چک کند (09 شروع + 11 رقم)
      if (value && value.trim() !== "") {
        const mobileRegex = /^09\d{9}$/;
        if (!mobileRegex.test(value)) {
          return "شماره موبایل باید با 09 شروع شده و 11 رقم باشد";
        }
      }
      break;

    case 'status':
      if (!['pending', 'approved', 'rejected', 'spam' , "deleted"].includes(value)) {
        return "وضعیت نامعتبر است";
      }
      break;

    default:
      break;
  }
  return '';
};

const EditComment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const commentData = location.state?.commentData;

  const [formData, setFormData] = useState({
    comment_text: '',
    status: 'pending',
    user_name: '',
    user_email: '',
    mobile: ''
  });

  const [csrfToken, setCsrfToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    // دریافت توکن CSRF
    fetch(`${BASE_URL}/csrf-token`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setCsrfToken(d.csrfToken));
      
    // اگر داده از طریق state نیامده بود، از API دریافت کنیم
    if (!commentData) {
      fetchComment();
    } else {
      setFormData({
        comment_text: commentData.comment_text,
        status: commentData.status,
        user_name: commentData.user?.name || '', // استفاده از user.name
        user_email: commentData.user?.email || '', // استفاده از user.email
        mobile: commentData.user?.mobile || '' // استفاده از user.mobile
      });
      setFetchLoading(false);
    }
  }, []);

  const fetchComment = async () => {
    try {
      const response = await fetch(`${BASE_URL}/admin/comments/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('خطا در دریافت نظر');
      
      const data = await response.json();
      setFormData({
        comment_text: data.comment_text,
        status: data.status,
        user_name: data.user_name || '',
        user_email: data.user_email || '',
        mobile: data.mobile || ''
      });
    } catch (error) {
      setMessage('خطا در دریافت اطلاعات نظر');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const errors = {};
    Object.keys(formData).forEach(name => {
      errors[name] = validateField(name, formData[name]);
    });
    setFieldErrors(errors);
    return Object.values(errors).every(error => !error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!validateAll()) {
      setMessage('لطفاً خطاهای فرم را برطرف کنید');
      return;
    }

     // اعتبارسنجی فقط فیلدهای پر شده
    const errors = {};
    Object.keys(formData).forEach(name => {
      if (formData[name] || name === 'status') { // وضعیت همیشه بررسی شود
        errors[name] = validateField(name, formData[name]);
      }
    });

    setFieldErrors(errors);

    // اگر خطایی وجود داشت
    if (Object.values(errors).some(error => error)) {
      setMessage('لطفاً خطاهای فرم را برطرف کنید');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/admin/comments/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.message || 'خطا در ویرایش نظر');
      
      setMessage('نظر با موفقیت ویرایش شد');
      setTimeout(() => navigate('/admin/comments/list'), 1500);
    } catch (error) {
      setMessage(error.message || 'خطا در ویرایش نظر');
    } finally {
      setIsLoading(false);
    }
  };

  if (fetchLoading) return <div className="loading">در حال بارگذاری...</div>;

  return (
    <div className="edit-comment-container">
      <h2>ویرایش نظر</h2>
      
      {message && (
        <div className={`alert ${message.includes('موفقیت') ? 'alert-success' : 'alert-danger'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>متن نظر</label>
          <textarea
            name="comment_text"
            value={formData.comment_text}
            onChange={handleChange}
            rows="5"
            className="form-control"
          />
          {fieldErrors.comment_text && (
            <div className="validation-error">{fieldErrors.comment_text}</div>
          )}
        </div>

        <div className="form-group">
          <label>وضعیت</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="form-control"
            required
          >
            <option value="pending">در انتظار بررسی</option>
            <option value="approved">تایید شده</option>
            <option value="rejected">رد شده</option>
            <option value="spam">اسپم</option>
            <option value="deleted">حذف شده</option>
          </select>
          {fieldErrors.status && (
            <div className="validation-error">{fieldErrors.status}</div>
          )}
        </div>

        {!formData.user_id && (
          <>
            <div className="form-group">
              <label>نام کاربر (مهمان)</label>
              <input
                type="text"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>ایمیل کاربر (اختیاری)</label>
              <input
                type="email"
                name="user_email"
                value={formData.user_email}
                onChange={handleChange}
                className="form-control"
              />
              {fieldErrors.user_email && (
                <div className="validation-error">{fieldErrors.user_email}</div>
              )}
            </div>

            <div className="form-group">
          <label>شماره موبایل (اختیاری)</label>
          <input
            type="tel"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            className="form-control"
            placeholder="09xxxxxxxxx"
          />
          {fieldErrors.mobile && (
            <div className="validation-error">{fieldErrors.mobile}</div>
          )}
        </div>
          </>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </button>
          
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/comments')}
          >
            انصراف
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditComment;